import { FC, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StoreManagement from '@/components/store/StoreManagement';
import BaseDialog from '@/components/ui/BaseDialog';
import { useAppContext } from '@/provider/contexts';
import { useStoreDetails } from '@/mobile/hooks/useStoreDetails';
import { StoreMgmtOutletContext } from '@/types/pages/storeManagement/context';

const STORE_VALIDATION_POLL_INTERVAL_MS = 30_000;

const StoreMgmtPageLayout: FC = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const { manageStoreId, setManageStoreId, setManageStoreName } = useAppContext();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const {
        fetchStoreDetails,
        isStoreInvalid,
        storeDetailsError,
        storeInfo,
        storeSettings,
        isTimezoneReady,
        formatUtcToStoreTime,
        storeWallTimeToUtcTimestamp,
        storeWallTimeToUtcString,
    } = useStoreDetails({ autoFetch: true });

    useEffect(() => {
        if (!isStoreInvalid) return;
        setIsDialogOpen(true);
        setManageStoreId(null);
        setManageStoreName(null);
    }, [isStoreInvalid, setManageStoreId, setManageStoreName]);

    /* Re-validate periodically while the tab is visible, since there is no push notification for another admin deleting/deactivating the store. */
    useEffect(() => {
        const interval = setInterval(() => {
            if (manageStoreId && document.visibilityState === 'visible') {
                fetchStoreDetails();
            }
        }, STORE_VALIDATION_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchStoreDetails, manageStoreId]);

    /* Re-validate promptly when the user returns to the tab. */
    useEffect(() => {
        const handleVisible = () => {
            if (manageStoreId && document.visibilityState === 'visible') {
                fetchStoreDetails();
            }
        };
        document.addEventListener('visibilitychange', handleVisible);
        window.addEventListener('focus', handleVisible);
        return () => {
            document.removeEventListener('visibilitychange', handleVisible);
            window.removeEventListener('focus', handleVisible);
        };
    }, [fetchStoreDetails, manageStoreId]);

    const isStoreReady = Boolean(manageStoreId) && !storeDetailsError;

    const outletContext: StoreMgmtOutletContext = {
        manageStoreId,
        isStoreReady,
        isTimezoneReady,
        storeDetailsError,
        storeInfo,
        storeSettings,
        fetchStoreDetails,
        formatUtcToStoreTime,
        storeWallTimeToUtcTimestamp,
        storeWallTimeToUtcString,
    };

    return (
        <>
            <StoreManagement storeId={manageStoreId} onStoreChange={setManageStoreId} />
            <Outlet context={outletContext} />
            <BaseDialog
                open={isDialogOpen}
                dialogTitle={t('Store Unavailable')}
                cancelButtonText=""
                confirmButtonText={t('Re-Select Store')}
                onConfirm={() => {
                    setIsDialogOpen(false);
                    navigate('/stores');
                }}
            >
                {storeDetailsError ? t(storeDetailsError) : ''}
            </BaseDialog>
        </>
    );
};

export default StoreMgmtPageLayout;
